import React, { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import PageErrorBoundary from "../components/misc/PageErrorBoundary";
import { toast } from "react-toastify";
import TopBar from "../components/navigation-bars/TopBar";
import SideBar from "../components/navigation-bars/SideBar";
import { User } from "../types/user";
import { useIsMobile } from "../hooks/useIsMobile";
import { SocketProvider } from "../context/SocketContext";
import CustomToast from "../components/misc/CustomToast";
import ImpersonationBanner from "../components/misc/ImpersonationBanner";
import useAppSelector from "../hooks/useAppSelector";
import { useImpersonationRefresh } from "../hooks/useImpersonationRefresh";

interface Props {
    isAdmin: boolean;
    user: User | null;
    isAdminView: boolean;
    maintenance: boolean;
    maintenanceMessage: string | null;
}

const UserLayout: React.FC<Props> = ({
    isAdmin,
    user,
    isAdminView,
    maintenance,
    maintenanceMessage
}) => {
    const isMobile = useIsMobile(900);
    const { isImpersonating } = useAppSelector((state) => state.auth);
    const bannerRef = useRef<HTMLDivElement>(null);
    const [bannerHeight, setBannerHeight] = useState(52);

    // Handle impersonation refresh behavior
    useImpersonationRefresh();

    // Check for interrupted uploads on mount
    useEffect(() => {
        const uploadsInterrupted = sessionStorage.getItem('uploadsInterrupted');
        if (uploadsInterrupted === 'true') {
            sessionStorage.removeItem('uploadsInterrupted');
            // Delay to ensure ToastContainer is ready
            setTimeout(() => {
                toast.error(
                    <CustomToast
                        message="Uploads were interrupted by a page refresh. Please restart the remaining uploads."
                        type="error"
                    />,
                    { autoClose: 5000 }
                );
            }, 100);
        }
    }, []);

    useEffect(() => {
        if (!isImpersonating || !bannerRef.current) return;
        const observer = new ResizeObserver((entries) => {
            const height = entries[0]?.contentRect.height;
            if (height) setBannerHeight(height);
        });
        observer.observe(bannerRef.current);
        setBannerHeight(bannerRef.current.getBoundingClientRect().height);
        return () => observer.disconnect();
    }, [isImpersonating]);

    useEffect(() => {
        const setVh = () => {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty("--vh", `${vh}px`);
        };
        setVh();
        window.addEventListener("resize", setVh);
        return () => window.removeEventListener("resize", setVh);
    }, []);

    return (
      <SocketProvider>
        <div
          className="flex flex-col"
          style={{ height: "calc(var(--vh, 1vh) * 100)" }}
        >
          {/* Impersonation Banner - Fixed at top */}
          {isImpersonating && (
            <div ref={bannerRef} className="fixed top-0 left-0 right-0 z-50">
              <ImpersonationBanner />
            </div>
          )}

          {/* TopBar with fixed position */}
          <header
            className="fixed left-0 right-0 h-16 flex items-center px-4 flex-shrink-0 z-50 bg-white"
            style={{
              top: isImpersonating ? `${bannerHeight}px` : "0",
              paddingTop: "env(safe-area-inset-top)",
            }}
          >
            <TopBar isAdmin={isAdmin} user={user} isAdminView={isAdminView} />
          </header>

          {maintenance && isAdmin && (
            <div
              className="fixed left-0 right-0 z-40"
              style={{ top: isImpersonating ? `calc(${bannerHeight}px + 4rem)` : "4rem" }}
            >
              <div className="flex items-center flex-col justify-center h-16 bg-red-700">
                <h1 className="text-center text-2xl font-bold text-white">
                  Maintenance Mode
                </h1>
              {maintenanceMessage && <p className="text-center text-sm font-semibold text-gray-200">{maintenanceMessage}</p>}
              </div>
            </div>
          )}

          {/* Main Content Area with padding for fixed header */}
          <div
            className="relative flex flex-1"
            style={{
              paddingTop: isImpersonating ? `calc(${bannerHeight}px + 4rem)` : "4rem",
            }}
          >
            {/* Desktop Sidebar - Fixed */}
            {!isMobile && (
              <aside
                id="app-sidebar"
                className="fixed left-0 bottom-0 bg-background text-white z-50"
                style={{
                  top: isImpersonating ? `calc(${bannerHeight}px + 4rem)` : "4rem",
                  height: isImpersonating
                    ? `calc(100vh - ${bannerHeight}px - 4rem)`
                    : "calc(100vh - 4rem)",
                  zIndex: 999,
                }}
              >
                <SideBar />
              </aside>
            )}

            {/* Page Content with padding for sidebar */}
            <main
              className={`relative flex-1 bg-background overscroll-contain scrolling-touch overflow-x-clip ${
                !isMobile ? "ml-16" : "h-full"
              }`}
              style={{
                paddingBottom: isMobile
                  ? "calc(env(safe-area-inset-bottom) + 80px)"
                  : "0",
                marginTop: maintenance && isAdmin ? "4rem" : "0",
              }}
            >
              <PageErrorBoundary>
                <Outlet />
              </PageErrorBoundary>
            </main>
          </div>

          {/* Mobile Sidebar - Fixed at bottom */}
          {isMobile && <SideBar />}
        </div>
      </SocketProvider>
    );
};

export default UserLayout;
